// src/pages/beta-community/BetaInscriptionForm.jsx
import { BRAND_NAME } from "@/config/brand";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import "./BetaInscriptionForm.css";

const BetaInscriptionForm = ({ disabledPlans = [], availabilityInfo = null } = {}) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    fonction: "",
    societe: "",
    profil: "",
    nb_oeuvres: "",
    nb_clients: "",
    plan: "",
    outils_actuels: "",
    pain_points: "",
    motivation: "",
    disponibilite_feedback: "",
    temoignage: "",
    disponibilite_appel: "",
    consent_confidentialite: false,
    consent_feedback: false,
    consent_rgpd: false,
    consent_contrat: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState("error"); // 'error', 'info', 'success'

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Reset message state à chaque submit (important)
    setError("");
    setErrorType("error");

    try {
      // Insert + vérif doublon + récupération du token, en un seul aller-retour côté serveur
      // (RPC SECURITY DEFINER : la table elle-même n'est plus lisible/écrivable directement
      // par un visiteur non connecté, cf. correctif sécurité sur beta_inscriptions).
      const { data: result, error: rpcError } = await supabase.rpc("submit_beta_inscription", {
        p_data: formData,
      });

      if (rpcError) {
        console.error("❌ Erreur submit_beta_inscription:", rpcError);
        setErrorType("error");
        setError("Une erreur est survenue. Veuillez réessayer.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        setLoading(false);
        return;
      }

      if (!result?.success) {
        if (result?.error === "duplicate_email") {
          setErrorType("info");
          setError(
            `Vous êtes déjà inscrit(e) !\n\n` +
              `L'adresse ${formData.email} figure déjà dans notre programme beta.\n\n` +
              `Pas reçu d'email ? Contactez-nous : support@artkiv.com`
          );
        } else {
          setErrorType("error");
          setError("Une erreur est survenue. Veuillez réessayer.");
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
        setLoading(false);
        return;
      }

      // Envoi de l'email de confirmation (best-effort)
      try {
        const { error: emailError } = await supabase.functions.invoke("send-beta-confirmation", {
          body: {
            inscription_id: result.id,
            email: result.email,
            prenom: result.prenom,
            edit_token: result.edit_token,
          },
        });

        if (emailError) {
          console.warn("⚠️ Email non envoyé:", emailError);
        }
      } catch (emailErr) {
        console.warn("⚠️ Exception envoi email:", emailErr);
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("❌ Erreur submit:", err);
      setErrorType("error");
      setError(err?.message ? err.message : "Une erreur est survenue.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="beta-inscription-page" style={{ fontFamily: "'Georgia', serif" }}>
        <div className="container">
          <div className="success-container">
            <div className="success-icon">✓</div>
            <h1 className="success-title">Candidature envoyée avec succès !</h1>
            <p className="success-text">
              Merci pour votre intérêt. Nous vous recontacterons sous 24-48h pour organiser notre call de validation de 15
              minutes.
            </p>
            <button className="btn btn-primary" onClick={() => navigate("/rejoindre-la-communaute")}>
              Retour à l&apos;accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="beta-inscription-page" style={{ fontFamily: "'Georgia', serif" }}>
      <div className="form-header">
        <h1 className="form-logo">{BRAND_NAME}</h1>
        <p className="form-subtitle">Inscription Beta Community</p>
      </div>

      <div className="container">
        <div className="form-intro">
          <p className="intro-highlight">Rejoignez les premiers testeurs fondateurs</p>
          <p>
            Présentez votre profil pour rejoindre le parcours bêta le plus adapté : collectionneur, Art Advisor, expert, Family Office ou CGP. Ce formulaire prend environ 5 minutes.
          </p>
        </div>

        {error && (
          <div className={`message ${errorType}-message`}>
            <span className="message-icon">
              {errorType === "error" && "⚠️"}
              {errorType === "info" && "💡"}
              {errorType === "success" && "✅"}
            </span>
            {error.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < error.split("\n").length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="beta-form">
          {/* Section 1: Informations personnelles */}
          <div className="form-section">
            <h2 className="section-title">1. Informations personnelles</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nom">
                  Nom <span className="required">*</span>
                </label>
                <input type="text" id="nom" name="nom" value={formData.nom} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="prenom">
                  Prénom <span className="required">*</span>
                </label>
                <input type="text" id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">
                  Email professionnel <span className="required">*</span>
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="telephone">
                  Téléphone <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fonction">Fonction / Titre</label>
                <input
                  type="text"
                  id="fonction"
                  name="fonction"
                  value={formData.fonction}
                  onChange={handleChange}
                  placeholder="Ex: Collectionneur, Art Advisor, Expert, CGP..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="societe">Société / Structure (si applicable)</label>
                <input
                  type="text"
                  id="societe"
                  name="societe"
                  value={formData.societe}
                  onChange={handleChange}
                  placeholder="Nom de votre entreprise ou cabinet"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Profil utilisateur */}
          <div className="form-section">
            <h2 className="section-title">2. Votre profil</h2>

            <div className="form-group">
              <label>
                Type de profil <span className="required">*</span>
              </label>
              <div className="radio-group">
                {[
                  { value: "collectionneur", title: "Collectionneur", desc: "Je gère ma propre collection d'art" },
                  { value: "art_advisor", title: "Art Advisor / Consultant", desc: "Je conseille des clients collectionneurs" },
                  { value: "expert", title: "Expert / Commissaire", desc: "Je suis expert indépendant ou commissaire d'exposition" },
                  { value: "family_office", title: "Family Office / CGP", desc: "Je supervise le patrimoine artistique de plusieurs familles ou clients" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`radio-option ${formData.profil === option.value ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="profil"
                      value={option.value}
                      checked={formData.profil === option.value}
                      onChange={handleChange}
                      required
                    />
                    <div className="radio-content">
                      <div className="radio-title">{option.title}</div>
                      <div className="radio-desc">{option.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nb_oeuvres">Nombre d&apos;œuvres sous gestion (approximatif)</label>
                <select id="nb_oeuvres" name="nb_oeuvres" value={formData.nb_oeuvres} onChange={handleChange}>
                  <option value="">Sélectionnez...</option>
                  <option value="0-50">0-50 œuvres</option>
                  <option value="50-200">50-200 œuvres</option>
                  <option value="200-500">200-500 œuvres</option>
                  <option value="500-1000">500-1000 œuvres</option>
                  <option value="1000+">Plus de 1000 œuvres</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="nb_clients">Nombre de clients ou familles suivis (pour Art Advisors, experts, FO/CGP)</label>
                <select id="nb_clients" name="nb_clients" value={formData.nb_clients} onChange={handleChange}>
                  <option value="">Sélectionnez...</option>
                  <option value="0">Non applicable</option>
                  <option value="1-5">1-5 clients</option>
                  <option value="5-20">5-20 clients</option>
                  <option value="20-50">20-50 clients</option>
                  <option value="50+">Plus de 50 clients</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 3: Plan souhaité */}
          <div className="form-section">
            <h2 className="section-title">3. Plan beta souhaité</h2>

            <div className="form-group">
              <label>
                Choisissez le parcours bêta à tester <span className="required">*</span>
              </label>
              <div className="radio-group">
                {[
                  {
                    value: "standard",
                    title: "Standard Collectionneur",
                    desc: "Inventaire, géolocalisation, documents, exports PDF/CSV",
                    price: "174€ (29€/mois × 6)",
                  },
                  {
                    value: "premium",
                    title: "Premium Collectionneur",
                    desc: "Standard + Vision IA, experts, conservation, valorisation, transmission",
                    price: "1.194€ (199€/mois × 6)",
                  },
                  {
                    value: "art_advisor",
                    title: "Art Advisor",
                    desc: "Gestion multi-clients (max 20), collections partagées, rapports",
                    price: "594€ (99€/mois × 6)",
                  },
                  {
                    value: "art_advisor_premium",
                    title: "Art Advisor Premium",
                    desc: "Clients illimités, lancement IA, rapports personnalisés, facturation",
                    price: "1.194€ (199€/mois × 6)",
                  },
                  {
                    value: "family_office",
                    title: "Family Office / CGP",
                    desc: "Vue consolidée multi-familles, reporting patrimonial, transmission",
                    price: "1.794€ (299€/mois × 6)",
                  },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`radio-option ${formData.plan === option.value ? "selected" : ""} ${disabledPlans.includes(option.value) ? "disabled" : ""}`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value={option.value}
                      checked={formData.plan === option.value}
                      onChange={handleChange}
                      disabled={disabledPlans.includes(option.value)}
                      required
                    />
                    <div className="radio-content">
                      <div className="radio-title">{option.title}</div>
                      <div className="radio-desc">{option.desc}</div>
                      <div className="radio-value">Valeur de 6 mois bêta : {option.price}</div>
                      {disabledPlans.includes(option.value) && <div className="radio-value">Parcours complet pour le moment</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Section 4: Motivation */}
          <div className="form-section">
            <h2 className="section-title">4. Votre motivation</h2>

            <div className="form-group">
              <label htmlFor="outils_actuels">Quels outils utilisez-vous aujourd’hui pour gérer vos collections ou celles de vos clients ?</label>
              <textarea
                id="outils_actuels"
                name="outils_actuels"
                value={formData.outils_actuels}
                onChange={handleChange}
                placeholder="Ex: Excel, Artlogic, Airtable, papier..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="pain_points">Quels sont vos principaux besoins, points de friction ou angles morts aujourd’hui ?</label>
              <textarea
                id="pain_points"
                name="pain_points"
                value={formData.pain_points}
                onChange={handleChange}
                placeholder="Décrivez ce qui vous manque dans vos outils actuels..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="motivation">Pourquoi souhaitez-vous tester {BRAND_NAME} ?</label>
              <textarea
                id="motivation"
                name="motivation"
                value={formData.motivation}
                onChange={handleChange}
                placeholder="Ce que vous aimeriez tester en priorité : inventaire, lots, estimation, rapports, IA, transmission, partage..."
              />
            </div>
          </div>

          {/* Section 5: Disponibilité */}
          <div className="form-section">
            <h2 className="section-title">5. Disponibilité</h2>

            <div className="form-group">
              <label>
                Pouvez-vous consacrer 15 à 30 minutes par mois à un retour d’usage ? <span className="required">*</span>
              </label>
              <div className="radio-group-inline">
                <label className="radio-option-inline">
                  <input
                    type="radio"
                    name="disponibilite_feedback"
                    value="oui"
                    checked={formData.disponibilite_feedback === "oui"}
                    onChange={handleChange}
                    required
                  />
                  <span>Oui</span>
                </label>
                <label className="radio-option-inline">
                  <input
                    type="radio"
                    name="disponibilite_feedback"
                    value="non"
                    checked={formData.disponibilite_feedback === "non"}
                    onChange={handleChange}
                  />
                  <span>Non, je ne peux pas m&apos;engager</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>
                Accepteriez-vous de fournir un témoignage ou retour d’expérience à la fin des 6 mois ? <span className="required">*</span>
              </label>
              <div className="radio-group-inline">
                <label className="radio-option-inline">
                  <input
                    type="radio"
                    name="temoignage"
                    value="oui_public"
                    checked={formData.temoignage === "oui_public"}
                    onChange={handleChange}
                    required
                  />
                  <span>Oui, témoignage public avec mon nom</span>
                </label>
                <label className="radio-option-inline">
                  <input
                    type="radio"
                    name="temoignage"
                    value="oui_anonyme"
                    checked={formData.temoignage === "oui_anonyme"}
                    onChange={handleChange}
                  />
                  <span>Oui, mais anonymisé</span>
                </label>
                <label className="radio-option-inline">
                  <input
                    type="radio"
                    name="temoignage"
                    value="non"
                    checked={formData.temoignage === "non"}
                    onChange={handleChange}
                  />
                  <span>Non</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="disponibilite_appel">Quand êtes-vous disponible pour un call de validation (15 min) ?</label>
              <textarea
                id="disponibilite_appel"
                name="disponibilite_appel"
                value={formData.disponibilite_appel}
                onChange={handleChange}
                placeholder="Ex: Lundis et mercredis matin, après-midis en semaine, etc."
              />
            </div>
          </div>

          {/* Section 6: Consentement */}
          <div className="consent-section">
            <h3 className="consent-title">Consentement et engagements</h3>

            <div className="checkbox-group">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="consent_confidentialite"
                  checked={formData.consent_confidentialite}
                  onChange={handleChange}
                  required
                />
                <span>
                  J&apos;accepte de ne pas partager publiquement les fonctionnalités en développement et les conditions bêta{" "}
                  <span className="required">*</span>
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="consent_feedback"
                  checked={formData.consent_feedback}
                  onChange={handleChange}
                  required
                />
                <span>
                  J&apos;accepte de fournir un retour mensuel constructif <span className="required">*</span>
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="consent_rgpd"
                  checked={formData.consent_rgpd}
                  onChange={handleChange}
                  required
                />
                <span>
                  J&apos;accepte que mes données soient traitées par Artkiv conformément au RGPD{" "}
                  <span className="required">*</span>
                </span>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  name="consent_contrat"
                  checked={formData.consent_contrat}
                  onChange={handleChange}
                  required
                />
                <span>
                  J&apos;ai compris qu&apos;un accord de participation bêta sera signé lors du cadrage{" "}
                  <span className="required">*</span>
                </span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="submit-container">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Envoi en cours..." : "Soumettre ma candidature bêta"}
            </button>
          </div>
        </form>
      </div>

      <footer className="form-footer">
        <p>
          <strong>Artkiv</strong> — Gestion, valorisation et transmission des collections
        </p>
        <p className="footer-links">
          <a href="mailto:support@artkiv.com">support@artkiv.com</a> · <a href="https://artkiv.com">artkiv.com</a>
        </p>
      </footer>
    </div>
  );
};

export default BetaInscriptionForm;
